import React from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  useTheme,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  School,
  Group,
  Psychology,
  Assessment,
  Timeline,
  BarChart,
  PieChart
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import TeacherLayout from '../../components/layout/TeacherLayout';
import StudentLayout from '../../components/layout/StudentLayout';

const AnalyticsIndex = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const analyticsModules = [
    {
      title: 'Student Performance Dashboard',
      description: 'Comprehensive analytics for individual student performance over time',
      icon: <TrendingUp color="primary" sx={{ fontSize: 40 }} />,
      path: '/analytics/student',
      features: [
        'Performance timeline',
        'Subject breakdown',
        'Topic analysis',
        'Time efficiency',
        'Recent activity'
      ],
      color: 'primary.main'
    },
    {
      title: 'Teacher Analytics',
      description: 'Monitor class performance and individual student progress',
      icon: <Group color="secondary" sx={{ fontSize: 40 }} />,
      path: '/analytics/teacher-wrapper',
      features: [
        'Class overview',
        'Student comparison',
        'Subject performance',
        'Performance trends',
        'Student-specific analytics'
      ],
      color: 'secondary.main',
      teacherOnly: true
    },
    {
      title: 'Confidence vs Accuracy',
      description: 'Analyze correlation between student confidence and actual performance',
      icon: <Psychology color="success" sx={{ fontSize: 40 }} />,
      path: '/analytics/confidence',
      features: [
        'Confidence calibration',
        'Overconfidence patterns',
        'Subject analysis',
        'Topic breakdown',
        'Insights & recommendations'
      ],
      color: 'success.main'
    },
    {
      title: 'Topic Performance',
      description: 'Detailed analysis of performance by specific topics and subjects',
      icon: <School color="info" sx={{ fontSize: 40 }} />,
      path: '/analytics/topics',
      features: [
        'Topic breakdown',
        'Difficulty analysis',
        'Performance trends',
        'Confidence correlation',
        'Test performance'
      ],
      color: 'info.main'
    },
    {
      title: 'Test Analytics',
      description: 'Analyze test performance, question difficulty, and student responses',
      icon: <Assessment color="warning" sx={{ fontSize: 40 }} />,
      path: '/analytics/tests',
      features: [
        'Test performance',
        'Question analysis',
        'Response patterns',
        'Time analysis',
        'Difficulty distribution'
      ],
      color: 'warning.main'
    },
    {
      title: 'Comparative Analysis',
      description: 'Compare performance across students, subjects, and time periods',
      icon: <BarChart color="error" sx={{ fontSize: 40 }} />,
      path: '/analytics/compare',
      features: [
        'Student comparison',
        'Subject comparison',
        'Time period analysis',
        'Performance ranking',
        'Trend analysis'
      ],
      color: 'error.main'
    }
  ];

  const handleModuleClick = (path) => {
    navigate(path);
  };

  // Filter modules based on user role
  const availableModules = analyticsModules.filter(module => 
    !module.teacherOnly || user?.role_id?.role_name === 'TEACHER'
  );

  // Determine which layout to use based on user role
  const LayoutComponent = user?.role_id?.role_name === 'TEACHER' ? TeacherLayout : StudentLayout;

  return (
    <LayoutComponent>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
        {/* Header */}
        <Box mb={4}>
          <Typography variant="h3" component="h1" gutterBottom>
            Analytics Dashboard
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Comprehensive insights into student performance and learning patterns
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Use these analytics tools to understand performance trends, identify areas for improvement, 
            and make data-driven decisions about teaching and learning strategies.
          </Typography>
        </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Timeline color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" component="div" color="primary">
              Performance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track student progress over time
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Psychology color="secondary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" component="div" color="secondary">
              Confidence
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analyze self-assessment accuracy
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <School color="success" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" component="div" color="success">
              Topics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Subject and topic breakdown
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Group color="info" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" component="div" color="info">
              Class
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overall class performance
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Analytics Modules */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Available Analytics Tools
      </Typography>
      
      <Grid container spacing={3}>
        {availableModules.map((module, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8]
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleModuleClick(module.path)}
                sx={{ height: '100%', p: 3 }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box mb={2}>
                    {module.icon}
                  </Box>
                  
                  <Typography variant="h6" component="h3" gutterBottom>
                    {module.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    {module.description}
                  </Typography>
                  
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Key Features:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="center">
                      {module.features.map((feature, featureIndex) => (
                        <Chip
                          key={featureIndex}
                          label={feature}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  <Chip
                    label="Explore"
                    color="primary"
                    variant="contained"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Usage Tips */}
      <Paper sx={{ p: 4, mt: 6, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          💡 How to Use Analytics Effectively
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              For Students:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" mb={1}>
                Review your performance trends over time
              </Typography>
              <Typography component="li" variant="body2" mb={1}>
                Identify topics that need more practice
              </Typography>
              <Typography component="li" variant="body2" mb={1}>
                Understand your confidence vs accuracy patterns
              </Typography>
              <Typography component="li" variant="body2" mb={1}>
                Set realistic learning goals based on data
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              For Teachers:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" mb={1}>
                Monitor class performance trends
              </Typography>
              <Typography component="li" variant="body2" mb={1}>
                Identify struggling students early
              </Typography>
              <Typography component="li" variant="body2" mb={1}>
                Adjust teaching strategies based on data
              </Typography>
              <Typography component="li" variant="body2" mb={1}>
                Track improvement over time
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Privacy Notice */}
      <Paper sx={{ p: 3, mt: 4, bgcolor: 'primary.50' }}>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          🔒 Data Privacy & Security
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All analytics data is anonymized and secure. Student performance data is only accessible to 
          authorized teachers and the students themselves. We follow strict data protection guidelines 
          to ensure your privacy and security.
        </Typography>
      </Paper>
      </Container>
    </LayoutComponent>
  );
};

export default AnalyticsIndex;
